import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Loader from '../../components/utils/Loader';
import useFetch from '../../hooks/useFetch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMicrophone
  } from '@fortawesome/free-solid-svg-icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

import { Line } from 'react-chartjs-2';
import AILightComponent from '../../components/AILightComponent';
import SpeechInputComponent from '../../components/SpeechInputComponent';
import { GenerateLCCode } from '../../components/GenerateThink5whysCode';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);

/* =========================
   MULTILINGUAL SUPPORT
========================= */
import HAFHTodoTranslations from './HAFHTodoTranslations';

const languages = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिंदी' },
  { code: 'mr', label: 'मराठी' },
  { code: 'gu', label: 'ગુજરાતી' },
  { code: 'bn', label: 'বাংলা' },
  { code: 'ta', label: 'தமிழ்' },
  { code: 'te', label: 'తెలుగు' },
  { code: 'kn', label: 'ಕನ್ನಡ' },
  { code: 'ml', label: 'മലയാളം' },
  { code: 'pa', label: 'ਪੰਜਾਬੀ' },
  { code: 'as', label: 'অসমীয়া' },
];

export default function HAFHApplauseHome() {
  // GLOBAL STATE
  const authState = useSelector(state => state.authReducer);
  const navigate = useNavigate();

  const [fetchData, { loading }] = useFetch();

  const user = authState?.user || {};

  const userId = user._id;
  const userCode = user.think5whyscode;
  const userName = user.name;

  // LANGUAGE
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const t = (key) => HAFHTodoTranslations[selectedLanguage]?.[key] || key;

  // REFS
  const applauseTitleRef = useRef(null);
  const listSectionRef = useRef(null);
  const createSectionRef = useRef(null);
  const reportsSectionRef = useRef(null);

  // UI STATE
  const [activeTab, setActiveTab] = useState('List');
  const [filter, setFilter] = useState("ALL");
  const [dateFilter, setDateFilter] = useState("ALL");
  const [actionFilter, setActionFilter] = useState("ALL");

  const [message, setMessage] = useState({ type: "", text: "" });
  const showMessage = (text, type = "success") => setMessage({ text, type });

  const [activeAssistantId, setActiveAssistantId] = useState(null);

  // APPLAUSE FORM STATE
  const [applauseList, setApplauseList] = useState([]);
  const [applauseCode, setApplauseCode] = useState("");
  const [applauseTo, setApplauseTo] = useState("");
  const [applauseFor, setApplauseFor] = useState("");
  const [isSelfApplause, setIsSelfApplause] = useState(true);
  const [nameCode, setNameCode] = useState("");
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [remarks, setRemarks] = useState("");
  const [isReply, setIsReply] = useState(false);
  const [reply, setReply] = useState("");
  const [whoSuggested, setWhoSuggested] = useState("Self-created");
  const [tags, setTags] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [applauseDate, setApplauseDate] = useState(new Date().toISOString().split("T")[0]);
  const [replyDate, setReplyDate] = useState("");

  // EDITING STATE
  const [isEditing, setIsEditing] = useState(false);
  const [editingApplauseId, setEditingApplauseId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  // VOICE/UI/AI STATE
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [activeTtsId, setActiveTtsId] = useState(null);
  const [isListSpeaking, setIsListSpeaking] = useState(false);
  const [showMicrophoneAlert, setShowMicrophoneAlert] = useState(false);
  const [activeAssistantApplauseId, setActiveAssistantApplauseId] = useState(null);
  const [activeAssistantCreateApplause, setActiveAssistantCreateApplause] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [showAIHelp, setShowAIHelp] = useState(false);

  const isSameDay = (d1, d2) =>new Date(d1).toDateString() === new Date(d2).toDateString();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const getWeekRange = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diffToMonday = d.getDate() - day + (day === 0 ? -6 : 1);
    const start = new Date(d.setDate(diffToMonday));
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { start, end };
  };

  const getMonthRange = (date, offset = 0) => {
    const d = new Date(date);
    const start = new Date(d.getFullYear(), d.getMonth() + offset, 1);
    const end = new Date(d.getFullYear(), d.getMonth() + offset + 1, 0);
    return { start, end };
  };
  
  const getFilteredApplause = () => {
    const today = startOfDay(new Date());
    return applauses.filter((applause) => {
      if (!applause.applauseDate) return dateFilter === "ALL";
      const d = startOfDay(new Date(applause.applauseDate));
      const diffDays = (d - today) / (1000 * 60 * 60 * 24);
      // basic filters
      if (dateFilter === "today") return diffDays === 0;
      if (dateFilter === "yesterday") return diffDays === -1;
      if (dateFilter === "tomorrow") return diffDays === 1;
      // week filters
      if (dateFilter === "thisweek") {
        const { start, end } = getWeekRange(new Date());
        return d >= start && d <= end;
      }
      if (dateFilter === "lastweek") {
        const { start, end } = getWeekRange(new Date());
        start.setDate(start.getDate() - 7);
        end.setDate(end.getDate() - 7);
        return d >= start && d <= end;
      }
      if (dateFilter === "nextweek") {
        const { start, end } = getWeekRange(new Date());
        start.setDate(start.getDate() + 7);
        end.setDate(end.getDate() + 7);
        return d >= start && d <= end;
      }
      // month filters
      if (dateFilter === "thismonth") {
        const { start, end } = getMonthRange(new Date());
        return d >= start && d <= end;
      }
      if (dateFilter === "lastmonth") {
        const { start, end } = getMonthRange(new Date(), -1);
        return d >= start && d <= end;
      }
      if (dateFilter === "nextmonth") {
        const { start, end } = getMonthRange(new Date(), 1);
        return d >= start && d <= end;
      }
      return true;
    });
  };
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setTimeout(() => {
      const sectionMap = {
        [t('list')]: listSectionRef,
        [t('create')]: createSectionRef,
        [t('reports')]: reportsSectionRef,
      };
      const selectedRef = sectionMap[tab];
      selectedRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

    }, 100);
  };

  const tabs = [
    t('list'),
    t('create'),
    t('reports'),
  ];

  const speakText = (data) => {
    if (!window.speechSynthesis) {
      showMessage("Text to Speech is not supported in this browser", "error");
      return;
    }
    speechSynthesis.cancel();
    let text = "";

    // CASE 1: array of applauses
    if (Array.isArray(data)) {
      text = data
        .map(t => `Title: ${t.title || ""}. Category: ${t.category || ""}. Description: ${t.desc || ""}.`)
        .join(" ");
    }

    // CASE 2: string list (your current button)
    else if (typeof data === "string") {
      text = data;
    }

    // CASE 3: single applause object
    else if (typeof data === "object" && data !== null) {
      text = `Title: ${data.title || ""}. Category: ${data.category || ""}. Description: ${data.desc || ""}.`;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = selectedLanguage === "hi" ? "hi-IN" : "en-US";
    utterance.rate = 1;
    utterance.pitch = 1;

    utterance.onstart = () => {
      setIsSpeaking(true);
      setIsPaused(false);
    };

    utterance.onend = () => {
      setIsSpeaking(false);
      setIsPaused(false);
      setActiveTtsId(null);
    };

    utterance.onerror = () => {
      setIsSpeaking(false);
      setIsPaused(false);
      setActiveTtsId(null);
      showMessage("Error occurred while speaking", "error");
    };

    speechSynthesis.speak(utterance);
  };

  const pauseSpeech = () => { speechSynthesis.pause(); setIsPaused(true); };
  const resumeSpeech = () => { speechSynthesis.resume(); setIsPaused(false); };
  const stopSpeech = () => {
    speechSynthesis.cancel();
    setIsSpeaking(false);
    setIsPaused(false);
    setActiveTtsId(null);
  };

  // =========================
  // FETCH DATA
  // =========================
  const fetchApplause = async () => {
    try {
      const config = {
        url: "/hafhapplause/myapplause",
        method: "post",
        data: { userId },
        headers: { Authorization: authState.token },
      };

      const res = await fetchData(config, { silent: true });

      if (res?.status) {

        const formatted = res.applauses.map((a) => ({
          _id: a._id,
          applauseDate: a.applauseDate
            ? a.applauseDate.split("T")[0]
            : "",
          applauseTo: a.applauseTo,
          applauseFor: a.applauseFor,
          name: a.name,
          nameCode: a.nameCode,
          title: a.title,
          description: a.description,
          remarks: a.remarks,
          reply: a.reply,
          tags: a.tags || [],
          isReply: a.isReply,
          isSelfApplause: a.isSelfApplause,
          isActive: a.isActive,
        }));

        setApplauseList(formatted);
      }

    } catch (err) {
      showMessage("Failed to load applause", "error");
    }
  };


  useEffect(() => {
      if (userId) fetchApplause();
    }, [userId]);

  const handleCancelApplause = () => resetForm();

  // =========================
  // RESET FORM
  // =========================
  const resetForm = () => {
    setApplauseCode("");
    setApplauseDate(new Date().toISOString().split("T")[0]);
    setApplauseTo("");
    setApplauseFor("");
    setNameCode(""),
    setName("");
    setTitle("");
    setDescription("");
    setRemarks("");
    setReply("");
    setTags([]);
    setIsActive(true);
    setIsEditing(false);
    setEditingApplauseId(null);   
  };

  // EDIT
  const handleEditApplause = (a) => {
    setIsEditing(true);
    // console.log(a);
    setEditingApplauseId(a._id);
    setApplauseCode(a.applauseCode);
    setNameCode(a.nameCode || "");
    setApplauseTo(a.applauseTo || "SELF");
    setApplauseFor(a.applauseFor || "");
    setName(a.name || "");
    setTitle(a.title || "");
    setDescription(a.description || "");
    setRemarks(a.remarks || "");
    setReply(a.reply || "");
    setTags(a.tags || []);
    setApplauseDate(a.applauseDate || "");
    setReplyDate(a.replyDate || "");
    setIsActive(a.isActive ?? true);

    // window.scrollTo({ top: 0, behavior: "smooth" });
    createSectionRef.current?.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
  };

  const handleSaveApplause = async () => {
    if (!title || !description) {
      return showMessage("Title & Description required", "error");
    }
    setIsSaving(true);
    try {
      const payload = {
        user: userId,
        applauseCode,
        nameCode,
        applauseTo,
        applauseFor,
        name,
        title: title?.trim(),
        description: description?.trim(),
        remarks,
        reply,
        tags: Array.isArray(tags)
          ? tags
          : tags.split(",").map((t) => t.trim()).filter(Boolean),
        applauseDate: applauseDate ? new Date(applauseDate) : new Date(),
        replyDate: replyDate ? new Date(replyDate) : null,
        isActive,
      };

      const config = {
        url: "/hafhapplause",
        method: "post",
        data: payload,
        headers: { Authorization: authState.token },
      };

      const res = await fetchData(config, { silent: true });

      if (res?.status) {
        showMessage("Saved successfully");
        resetForm();
        fetchApplause();
      }
    } catch (err) {
      // console.log(err);
      showMessage("Save failed", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // UPDATE
  const handleUpdateApplause = async () => {
    try {
      const payload = {
        applauseTo,
        applauseFor,
        nameCode,
        name,
        title,
        description,
        remarks,
        reply,
        tags: Array.isArray(tags)
          ? tags
          : tags.split(",").map((t) => t.trim()).filter(Boolean),

        applauseDate: applauseDate ? new Date(applauseDate) : null,
        replyDate: replyDate ? new Date(replyDate) : null,
        isActive,
      };
      const config = {
        url: `/hafhapplause/${editingApplauseId}`,
        method: "put",
        data: payload,
        headers: { Authorization: authState.token },
      };

      const res = await fetchData(config, { silent: true });

      if (res?.status) {
        showMessage("Updated successfully");
        resetForm();
        fetchApplause();
      }
    } catch {
      showMessage("Update failed", "error");
    }
  };

  const handleDeleteApplause = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      const config = {
        url: `/hafhapplause/${editingApplauseId}`,
        method: "delete",
        headers: {
          Authorization: authState.token,
        },
      };
      // const response = await fetchData(config);
      const response = await fetchData(config, { silent: true });
      if (response?.status) {
        await fetchApplause();
        resetForm();
        showMessage("Record Deleted Successfully");
      }
    } catch (error) {
      console.error("DELETE Applause ERROR:", error);
      showMessage(
        error?.response?.data?.msg ||
        error?.message ||
        "Failed to delete Applause",
        "error"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  // FILTER
  const filtered = applauseList.filter((applause) => {
    const applauseDateObj = applause.applauseDate
      ? new Date(applause.applauseDate)
      : null;

    const now = new Date();
    let matchesDate = true;
    if (applauseDateObj) {
      const applauseDay = new Date(
        applauseDateObj.getFullYear(),
        applauseDateObj.getMonth(),
        applauseDateObj.getDate()
      );
      const today = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate()
      );
      if (dateFilter === "today") {
        matchesDate =
          applauseDay.getTime() === today.getTime();
      }
      else if (dateFilter === "thisweek") {
        const startOfWeek = new Date(today);
        const day = startOfWeek.getDay();
        const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
        startOfWeek.setDate(diff);
        startOfWeek.setHours(0, 0, 0, 0);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);
        matchesDate =
          applauseDateObj >= startOfWeek &&
          applauseDateObj <= endOfWeek;
      }

      else if (dateFilter === "thismonth") {
        matchesDate =
          applauseDateObj.getMonth() === now.getMonth() &&
          applauseDateObj.getFullYear() === now.getFullYear();
      }
    }

    // ACTION FILTER
    let matchesAction = true;
    if (actionFilter === "myself") {
      matchesAction = applause.isSelfApplause === true;
    }
    else if (actionFilter === "tome") {
      matchesAction =
        applause.nameCode === userCode &&
        applause.isSelfApplause === false;
    }
    else if (actionFilter === "fromme") {
      matchesAction =
        applause.isSelfApplause === false &&
        applause.nameCode !== userCode;
    }
    return matchesDate && matchesAction;
  });

  const selfCount = applauseList.filter(a => a.isSelfApplause).length;
  const toMeCount = applauseList.filter(a => a.nameCode === userCode && !a.isSelfApplause).length;
  const fromMeCount = applauseList.filter(a => !a.isSelfApplause && a.nameCode !== userCode).length;

  const toggleVoiceInput = () => {
    if (isVoiceListening) {
      stopVoiceInput();
      return;
    }
    startVoiceInput();
  };
  
  const startVoiceInput = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      showMessage("Speech Recognition not supported in this browser", "error");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-IN";           
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      let transcript = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      if (transcript) {
        setApplauseDetails(prev => prev + " " + transcript);
      }
    };

    recognition.onerror = (event) => {
      console.error(event);
      if (event.error !== "no-speech") {
        showMessage("Voice recognition error. Please try again.", "error");
        stopVoiceInput();
      }
    };

    recognition.onend = () => {
      setIsVoiceActive(false);
      setIsVoicePaused(false);
    };

    try {
      recognition.start();
      setRecognitionInstance(recognition);
      setIsVoiceActive(true);
      setIsVoicePaused(false);
      
      // Auto focus on textarea
      document.getElementById("description")?.focus();
      
      showMessage("🎤 Listening... Speak now", "success");
    } catch (err) {
      showMessage("Failed to start voice input", "error");
    }
  };
  
  const pauseVoiceInput = () => {
    if (recognitionInstance) recognitionInstance.stop();
    setIsVoicePaused(true);
    showMessage("Voice input paused", "success");
  };
  
  const resumeVoiceInput = () => {
    setIsVoicePaused(false);
    startVoiceInput();
  };
  
  const stopVoiceInput = () => {
    if (recognitionInstance) recognitionInstance.stop();
    setIsVoiceActive(false);
    setIsVoicePaused(false);
    setRecognitionInstance(null);
    showMessage("Voice input stopped", "success");
  };
  
  const handleMicrophoneClick = () => {
    setShowMicrophoneAlert(true);
  };

  const buildSingleApplauseSpeech = (applause) => {
    const parts = [];
    parts.push(`Let me read out this applause to you.`);
    if (applause?.applauseFor) parts.push(`This Applause is for ${applause.applauseFor}.`);
    if (applause?.description) parts.push(`And the applause statement is ${applause.description}.`);
    return parts.join(" ");
  };
  
  // ====================== CHART ======================
  const chartData = useMemo(() => ({
    labels: [t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')],
    datasets: [
      {
        label: t('applausePercent'),
        data: [65, 72, 70, 80, 82, 88],
        borderColor: '#14b8a6',
        backgroundColor: 'rgba(20,184,166,0.18)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: '#fde68a',
      },
    ],
  }), [selectedLanguage]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { color: '#fde68a' } },
    },
    scales: {
      x: { ticks: { color: '#d1d5db' }, grid: { color: 'rgba(255,255,255,0.05)' } },
      y: { beginAtZero: true, ticks: { color: '#d1d5db', stepSize: 20 }, grid: { color: 'rgba(255,255,255,0.05)' } },
    },
  }), []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-slate-950 to-slate-900 text-gray-100">
      <div className="mx-auto max-w-7xl space-y-2">
        <div className="overflow-hidden rounded-3xl border border-slate-800 bg-gradient-to-r from-black via-slate-950 to-slate-900 p-5 shadow-2xl sm:p-8">
          <div className="flex flex-col gap-2 justify-start lg:flex-row">
            <div className="flex-1 text-left justify-between">
              <div className="mb-2 flex flex-row gap-3">
                <div className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-3 py-1 text-xs text-gray-200 backdrop-blur-sm">
                  <i className="fa-solid fa-bolt text-yellow-300"></i>
                  {t("Gratitude")}
                </div>
                <div className="flex flex-col gap-2 px-3 py-1">
                  <select
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                    className="w-full sm:w-48 rounded-sm border border-slate-700 bg-slate-900 px-3 py-1 text-xs lg:text-sm text-white outline-none transition-all focus:border-teal-500"
                  >
                    {languages.map((l) => (
                      <option key={l.code} value={l.code}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <h1 className="text-left text-xl lg:text-2xl font-black leading-tight text-yellow-200">{t("applause")}</h1>
              <p className="mt-2 text-left text-xs leading-7 text-gray-300 lg:text-sm">{t('applauseSub')}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="rounded-3xl border border-slate-800 bg-slate-900 p-3 shadow-xl">
          <div className="grid grid-cols-3 gap-2 lg:grid-cols-6">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`flex w-full items-center justify-center rounded-md px-2 py-1 text-center text-xs font-semibold transition-all lg:text-sm ${
                  activeTab === tab ? 'bg-teal-700 text-yellow-200 shadow-lg shadow-teal-900/30' : 'bg-slate-800 text-gray-200 hover:bg-slate-700 hover:text-yellow-200'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-5 xl:grid-cols-3">
          <div className="space-y-5 xl:col-span-2">
            {/* ====================== Applause LIST ====================== */}
            <div ref={listSectionRef} className="rounded-xl border border-slate-800 bg-slate-700 p-2 shadow-2xl">
              <div className="mb-2 flex flex-col gap-4">
                <div>
                  <h2 className="text-lg lg:text-xl font-black text-yellow-200">{t('applauseList')}</h2>
                  <p className="mt-1 text-sm text-gray-300">{t('applauseListDesc')}</p>
                </div>
                <div className="mb-1 grid grid-cols-2 lg:grid-cols-4 gap-1 border-b border-cyan-300 pb-2">
                  {[
                    { key: "all", label: t("all"), count: applauseList.length },
                    { key: "myself", label: t("myself"), count: selfCount },
                    { key: "tome", label: t("tome"), count: toMeCount },
                    { key: "fromme", label: t("fromme"), count: fromMeCount },
                  ].map((af) => (
                    <button
                      key={af.key}
                      onClick={() => setActionFilter(af.key)}
                      className={`rounded-md px-2 py-1 text-xs lg:text-sm transition-all flex items-center justify-between gap-2
                        ${
                          actionFilter === af.key
                            ? "bg-teal-600 text-white"
                            : "bg-slate-800 text-gray-300 hover:bg-slate-700"
                        }`}
                    >
                      <span>{af.label}</span>

                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded-full
                          ${
                            actionFilter === af.key
                              ? "bg-white/20 text-white"
                              : "bg-slate-700 text-cyan-300"
                          }`}
                      >
                        {af.count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-1 grid grid-cols-3 lg:grid-cols-4 gap-1 pb-2">
                {[
                  "all",
                  "today",
                  "thisweek",
                  "thismonth",
                ].map((f) => (
                  <button
                    key={f}
                    onClick={() => setDateFilter(f)}
                    className={`rounded-md px-1 py-1 text-xs lg:tex-sm transition-all
                      ${dateFilter === f
                        ? "bg-teal-600 text-white"
                        : "bg-slate-800 text-gray-300 hover:bg-slate-700"
                      }`}
                  >
                    {t(f)}
                  </button>
                ))}
                <button 
                  onClick={() => { 
                    setActiveTab(t('create'));
                    setTimeout(() => {
                      createSectionRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });

                    }, 100);
                }}
                  className="col-span-2 lg:col-span-2 text-xs lg:text-sm rounded-md bg-blue-700 px-1 py-2 font-bold text-white shadow-lg transition-all hover:bg-blue-600"
                >
                  <i className="fa-solid fa-plus mr-2"></i>{t('addApplause')}
                </button>
              </div>
              <div className='flex justify-end'>
                {!isListSpeaking ? (
                  <button
                    onClick={() => {
                      setIsListSpeaking(true);
                      const speechText = filtered
                        .map((applause, index) => {
                          return `
                            Applause ${index + 1}.
                            To: ${applause.applauseTo || "Not specified"}.
                            Title: ${applause.title || "Not specified"}.
                          `;
                        })
                        .join(" ");

                      speakText(speechText);
                    }}
                    className="w-64 rounded-md bg-yellow-600 px-3 py-2 text-xs font-bold text-black hover:bg-yellow-500"
                  >
                    <i className="fa-solid fa-volume-high mr-2"></i>
                    {t("listenList")}
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={() => { speechSynthesis.pause(); setIsPaused(true);}}
                      className="rounded-xl bg-blue-600 px-3 py-1 text-xs font-bold text-white"
                    >
                      <i className="fa-solid fa-pause mr-1"></i> Pause
                    </button>
                    <button
                      onClick={() => {speechSynthesis.resume();setIsPaused(false);}}
                      className="rounded-xl bg-yellow-600 px-3 py-1 text-xs font-bold text-black"
                    >
                      <i className="fa-solid fa-play mr-1"></i> Resume
                    </button>

                    <button
                      onClick={() => {
                        speechSynthesis.cancel();
                        setIsListSpeaking(false);
                        setIsPaused(false);
                        setActiveTtsId(null);
                      }}
                      className="rounded-xl bg-red-600 px-3 py-1 text-xs font-bold text-white"
                    >
                      <i className="fa-solid fa-stop mr-1"></i> Stop
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                {filtered.map((applause) => (
                  <div key={applause._id} className="rounded-3xl border border-slate-800 bg-slate-800 p-5 transition-all hover:border-teal-500 hover:shadow-lg">
                    <div className="flex flex-col gap-4">
                      <div className="flex-1">
                        <div className="mb-2 text-xs text-teal-300">
                          <i className="fa-regular fa-calendar mr-1"></i>
                          {applause.applauseDate
                            ? new Date(applause.applauseDate).toLocaleDateString("en-GB")
                            : "No Date"}
                          <span className="rounded-full bg-sky-700/20 px-3 py-1 text-xs font-semibold text-sky-300">
                            {applause.applauseTo}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-xs lg:text-sm font-bold text-yellow-200">{applause.title}</h3>
                          {applause.tts && (
                            <span className="rounded-full bg-orange-700/20 px-3 py-1 text-xs font-semibold text-orange-300">
                              <i className="fa-solid fa-volume-high mr-1"></i>{t('ttsEnabled')}
                            </span>
                          )}
                          <span className="rounded-full bg-sky-700/20 px-3 py-1 text-xs font-semibold text-sky-300">
                            {applause.applauseFor}
                          </span>
                        </div>
                        <p className="mt-3 text-sm leading-6 whitespace-pre-wrap break-words text-gray-300">{applause.description}</p>
                        <div className="mt-4 flex flex-wrap gap-2">
                          {applause.tags?.map((tag, i) => (
                            <span key={i} className="rounded-full bg-slate-800 px-3 py-1 text-xs font-medium text-gray-100">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="flex gap-2 bg-gray-800 border border-gray-600">
                        <div className={`flex gap-2 ${isListSpeaking ? "opacity-40 pointer-events-none" : ""}`}>
                          {activeTtsId !== applause._id ? (
                            <button onClick={() => {
                                setIsListSpeaking(true);
                                setActiveTtsId(applause._id);
                                const speechText = buildSingleApplauseSpeech(applause);
                                speakText(speechText);
                              }} 
                              className="w-10 lg:w-12 text-xs lg:text-sm rounded-xl bg-slate-800 px-1 py-1 text-yellow-200 hover:bg-teal-700">
                              <i className="fa-solid fa-play"></i>
                            </button>
                          ) : isPaused ? (
                            <button onClick={resumeSpeech} className="w-10 lg:w-12 text-xs lg:text-sm rounded-xl bg-yellow-700 px-1 py-1 text-black hover:bg-yellow-600">
                              <i className="fa-solid fa-play"></i>
                            </button>
                          ) : (
                            <>
                              <button onClick={pauseSpeech} className="w-10 lg:w-12 text-xs lg:text-sm rounded-xl bg-blue-700 px-1 py-1 text-white hover:bg-blue-600">
                                <i className="fa-solid fa-pause"></i>
                              </button>
                              <button onClick={stopSpeech} className="w-10 lg:w-12 text-xs lg:text-sm rounded-xl bg-red-700 px-1 py-1 text-white hover:bg-red-600">
                                <i className="fa-solid fa-stop"></i>
                              </button>
                            </>
                          )}
                        </div>
                        <button onClick={() => handleEditApplause(applause)} className="w-10 lg:w-12 text-xs lg:text-sm rounded-xl bg-slate-800 px-1 py-1 text-yellow-200 hover:bg-teal-700">
                          <i className="fa-solid fa-pen"></i>
                        </button>
                        <button
                          onClick={() => setActiveAssistantApplauseId(prev => prev === applause._id ? null : applause._id)}
                          className="w-10 lg:w-12 text-xs lg:text-sm rounded-xl bg-slate-800 px-1 py-1 text-yellow-200 hover:bg-teal-700"
                        >
                          <i className="fa-solid fa-robot"></i>
                        </button>
                      </div>
                    </div>
                    {activeAssistantApplauseId === applause._id && (
                      <div className="mt-2">
                        <AILightComponent
                          param1="hostel_applause"
                          param2={`
                            Title: ${applause?.title || ""}
                            Applause To: ${applause?.applauseTo || "Self"}
                            Applause For: ${applause?.applauseFor || ""}
                            Description: ${applause?.description || ""}
                            Remarks: ${applause?.remarks || ""}
                          `}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* ====================== CREATE / EDIT FORM ====================== */}
            <div ref={createSectionRef} className="rounded-3xl border border-slate-800 bg-slate-900 p-5 shadow-2xl sm:p-6">
              <div className="mb-2 flex items-start gap-4">
                <div className="rounded-2xl bg-slate-800 p-4 text-yellow-200">
                  <i className="fa-solid fa-square-plus text-lg lg:text-xl"></i>
                </div>
                <div>
                  <h2 className="text-lg lg:text-xl font-black text-yellow-200">
                    {isEditing ? "Edit To-Do" : t("createApplause")}
                  </h2>
                  <p className="mt-1 text-xs lg:text-sm text-gray-300">
                    {isEditing ? "Update the selected task" : t("createApplauseDesc")}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <div className='border border-blue-100 p-4 rounded-xl'>
                  <div>
                    <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                      {t("applauseDate")}*
                    </label>

                    <input
                      type="date"
                      value={applauseDate}
                      onChange={(e) => setApplauseDate(e.target.value)}
                      className="w-40 rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none transition-all focus:border-teal-500 focus:ring-4 focus:ring-teal-900/40"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">{t("title")}*</label>
                    <input
                      type="text"
                      ref={applauseTitleRef}
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder={"Enter Title"}
                      style={{width: "100%"}}
                      className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none transition-all focus:border-teal-500 focus:ring-4 focus:ring-teal-900/40"
                    />
                  </div>
                  <div className='flex flex-col space-y-1'>
                    <div className='flex flex-col space-y-1'>
                      <label className="text-xs font-bold text-yellow-200">
                        Applause To
                      </label>
                      <select
                        value={applauseTo}
                        onChange={(e) => {setApplauseTo(e.target.value);}}
                        className="w-64 rounded-2xl border border-slate-700 bg-slate-950 px-4 py-1 text-gray-100"
                      >
                        <option value="SELF">Self</option>
                        <option value="PARENT">Parent</option>
                        <option value="MANAGER">Manager</option>
                        <option value="OWNER">Owner</option>
                        <option value="MESS">Mess</option>
                        <option value="TRANSPORT">Transport</option>
                        <option value="FRIEND">Friend</option>
                        <option value="INSTITUTION">Institution</option>
                        <option value="ADMIN">Admin</option>
                        <option value="HUMA">HUMA</option>
                        <option value="OTHERS">OTHERS</option>
                      </select>
                    </div>
                    {applauseTo !== "SELF" && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <div className='flex flex-col space-y-1'>
                          <label className="text-xs font-bold text-yellow-200">
                            Person Code
                          </label>
                          <input
                            value={nameCode}
                            onChange={(e) => setNameCode(e.target.value)}
                            placeholder="Name Code"
                            className="w-80 rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none transition-all focus:border-teal-500 focus:ring-4 focus:ring-teal-900/40"
                          />
                        </div>
                        <div className='flex flex-col space-y-1'>
                          <label className="text-xs font-bold text-yellow-200">
                            Person Name
                          </label>
                          <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Name"
                            className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none transition-all focus:border-teal-500 focus:ring-4 focus:ring-teal-900/40"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className='flex flex-col space-y-1'>
                    <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">{t("applauseFor")}*</label>
                    <input
                      type="text"
                      value={applauseFor}
                      onChange={(e) => setApplauseFor(e.target.value)}
                      placeholder={"Applause For What"}
                      style={{width: "100%"}}
                      className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none transition-all focus:border-teal-500 focus:ring-4 focus:ring-teal-900/40"
                    />
                  </div>

                  <div className='flex flex-row gap-2 mt-4'>
                    <p className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">{t("details")}*</p>
                    <input 
                      type="checkbox" 
                      className="mr-2 cursor-pointer"
                      checked={showMicrophoneAlert} 
                      onChange={() => setShowMicrophoneAlert(!showMicrophoneAlert)} 
                    />
                    <span className="text-xs lg:text-sm font-medium text-gray-100">{t("evi")}</span>
                    <FontAwesomeIcon icon={faMicrophone} className="text-yellow-500 text-lg lg:text-xl ml-3" />
                  </div>
                  <div className="mb-2 text-xs lg:text-sm text-white">
                      { showMicrophoneAlert ? (
                          <SpeechInputComponent
                            label="Details"
                            name="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Click Mic and Speak or Talk for Voice Input..."
                            maxLength="2000"
                          />
                      ) : (
                        <textarea 
                          type="input" 
                          name="description" 
                          id="description" 
                          value={description}
                          placeholder={showMicrophoneAlert ? "Write here or Click Mic for Voice Input...":"Write here.."}
                          onChange={(e) => setDescription(e.target.value)}
                          maxLength="2000"
                          style={{
                            border: '1px solid #ccc',
                            padding: '2px',
                            backgroundColor: '#000000', 
                            color: '#f0f0f0',
                            maxWidth: '100%', 
                            maxHeight: '150px' 
                          }}
                        />
                      )}
                  </div>

                  <div className="flex flex-wrap items-start gap-3">
                    <button
                      onClick={() => setActiveAssistantCreateApplause(!activeAssistantCreateApplause)}
                      className="rounded-2xl bg-teal-700 px-2 py-3 font-bold text-white shadow-lg transition-all hover:bg-teal-600 w-40 lg:w-48 text-xs lg:text-sm"
                    >
                      <i className="fa-solid fa-wand-magic-sparkles mr-2"></i>
                      {t("refineWithAI")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAIHelp((prev) => !prev)}
                      className="h-10 w-10 rounded-full bg-slate-800 text-yellow-300 hover:bg-slate-700 transition-all flex items-center justify-center"
                    >
                      <i className="fa-solid fa-circle-question text-lg"></i>
                    </button>
                  </div>

                  {showAIHelp && (
                    <div className="mt-3 rounded-2xl border border-yellow-700 bg-slate-900 p-4 text-xs lg:text-sm text-gray-200 space-y-3">
                      <div className="text-yellow-200 font-bold">
                        How “Refine With AI” Works
                      </div>
                      <div>
                        Think5Whys can generate a structured AI-ready prompt for your task, complaint, idea, productivity note, recreation plan, expense entry,
                        or workflow requirement.
                      </div>
                      <div>
                        You can copy that generated prompt and paste it into any GenAI tool of your choice to improve, rewrite, structure, summarize,
                        expand, or optimize your description.
                      </div>
                      <div className="text-cyan-300">
                        Supported / Recommended AI Tools:
                      </div>
                      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 text-gray-100">
                        <div>• ChatGPT</div>
                        <div>• Claude</div>
                        <div>• Gemini</div>
                        <div>• Grok</div>
                        <div>• Perplexity</div>
                        <div>• Microsoft Copilot</div>
                        <div>• DeepSeek</div>
                        <div>• Meta AI</div>
                        <div>• Poe</div>
                        <div>• You.com AI</div>
                        <div>• Think5Whys Own AI</div>
                      </div>
                      <div>
                        Once you are satisfied with the AI-generated output, copy the improved response from that AI tool and paste it back into
                        the Think5Whys description field for better quality inputs, task clarity, automation accuracy, analytics, and execution support.
                      </div>
                      <div className="text-teal-300">
                        You can also take advantage of optional auto-integration workflows with your preferred GenAI tools using a seamless copy + paste assisted flow,
                        where Think5Whys can help send the generated prompt and receive the refined response more efficiently. This feature is paid and usage cost
                        may apply as per the active pricing plan of the selected GenAI tool. Pricing can vary across different AI providers and models.
                      </div>
                      <div className="text-orange-300">
                        Tip: More detailed and structured descriptions help Think5Whys workflows, AI analysis, task assignment, and reporting systems
                        perform better.
                      </div>
                    </div>
                  )}
                </div>

                {/* AI Refinement Section */}
                {activeAssistantCreateApplause && (
                  <>
                    {(!title.trim() || !description.trim()) ? (
                      <div className="mt-4 rounded-2xl border border-red-500 bg-red-500/10 p-4">
                        <div className="flex items-start gap-3 text-red-300">
                          <i className="fa-solid fa-circle-exclamation mt-1"></i>
                          <div>
                            <p className="font-bold text-yellow-200">{t("missingri")}</p>
                            <p className="mt-1 text-xs lg:text-sm text-red-200">
                              {t("plfillthefield")}{ 
                                [title.trim(), description.trim()].filter(Boolean).length === 3 ? '' : 's'
                              }:
                            </p>

                            <ul className="mt-2 text-xs lg:text-sm text-red-200 [&_li]:text-gray-100 list-disc list-inside space-y-1">
                              {!title.trim() && <li className='text-xs lg:text-sm'><strong>{t("title")}</strong></li>}
                              {!description.trim() && <li className='text-xs lg:text-sm'><strong>{t("description")}</strong></li>}
                            </ul>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2">
                        <AILightComponent
                            param1="hostel_applause_creation"
                            param2={`Type: ${applauseTo}
                                Self: ${isSelfApplause}
                                Name: ${name}
                                Category: ${applauseFor}
                                Title: ${title}
                                Description: ${description}
                                Tags: ${tags?.join(", ") || ""}`}
                          />
                      </div>
                    )}
                  </>
                )}

                <div className="mt-4">
                  <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                    {t("whoDrafted")}
                  </label>
                  <input
                    type="text"
                    value={whoSuggested}
                    onChange={(e) => setWhoSuggested(e.target.value)}
                    placeholder="e.g. Grok, ChatGPT, Claude, Self, Manager, etc."
                    className="w-full text-xs lg:text-sm rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-900/40"
                  />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
                  <div className='gap-2'>
                    <input 
                      type="checkbox" 
                      className="mr-2 cursor-pointer"
                      checked={isActive} 
                      onChange={(e) => setIsActive(e.target.checked)} 
                    />
                    <label className="mb-2 text-xs lg:text-sm font-bold text-yellow-200">Is Active (Not Visible)</label>
                  </div>
                </div>
                {isReply && (
                  <div className='gap-2'>
                    <input 
                      type="checkbox" 
                      className="mr-2 cursor-pointer"
                      checked={isReply} 
                      onChange={(e) => setIsReply(e.target.checked)} 
                    />
                    <label className="mb-2 text-xs lg:text-sm font-bold text-yellow-200">Reply</label>
                    <textarea 
                      type="input" 
                      name="reply" 
                      id="reply" 
                      value={reply}
                      placeholder={showMicrophoneAlert ? "Write here or Click Mic for Voice Input...":"Write here.."}
                      onChange={(e) => setReply(e.target.value)}
                      maxLength="2000"
                      style={{
                        border: '1px solid #ccc',
                        padding: '2px',
                        backgroundColor: '#000000', 
                        color: '#f0f0f0',
                        maxWidth: '100%', 
                        maxHeight: '150px' 
                      }}
                    />
                  </div>
                )}
                <div>
                  <label className="mb-2 text-xs lg:text-sm font-bold text-yellow-200">Additional Remarks</label>
                  <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Enter Remarks" style={{ height: "150px" }} className="w-full text-xs lg:text-sm rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none focus:border-teal-500" />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-2 grid grid-cols-4 lg:grid-cols-4 gap-1">
                <button
                  disabled={isEditing || isSaving || isSaved}
                  onClick={handleSaveApplause}
                  className={`w-40 lg:w-48 text-xs lg:text-sm rounded-xl px-2 py-1 font-bold text-white shadow-lg transition-all
                    ${(isEditing || isSaving)
                      ? "cursor-not-allowed bg-slate-700 opacity-50"
                      : "bg-teal-600 hover:bg-teal-500"
                    }`}
                >
                  <i className="fa-solid fa-floppy-disk mr-2"></i>
                  {isSaving ? "Saving..." : "Save Data"}
                </button>

                {isEditing && (
                  <>
                    <button onClick={() => handleUpdateApplause()} className="w-20 lg:w-32 text-xs lg:text-sm rounded-xl bg-yellow-600 px-2 py-1 font-bold text-black shadow-lg hover:bg-yellow-500">
                      <i className="fa-solid fa-rotate mr-2"></i> Update
                    </button>
                    <button onClick={handleDeleteApplause} disabled={isDeleting} className="w-20 lg:w-32 text-xs lg:text-sm rounded-xl bg-red-700 px-2 py-1 font-bold text-white shadow-lg hover:bg-red-600">
                      <i className="fa-solid fa-trash mr-2"></i> Delete
                    </button>
                  </>
                )}
                <button onClick={handleCancelApplause} className="w-20 lg:w-32 text-xs lg:text-sm rounded-xl bg-slate-700 px-2 py-1 font-bold text-white shadow-lg hover:bg-slate-600">
                  <i className="fa-solid fa-xmark mr-2"></i> Cancel
                </button>
              </div>
              {/* Message Banner */}
              {message.text && (
                <div className={`rounded-2xl border px-6 py-4 text-xs lg:text-sm font-medium flex items-center gap-3 shadow-lg ${
                  message.type === 'success' 
                    ? 'bg-green-900/50 border-green-500 text-green-200' 
                    : 'bg-red-900/50 border-red-500 text-red-200'
                }`}>
                  <i className={`fa-solid ${message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
                  <span>{message.text}</span>
                  <button 
                    onClick={() => setMessage({ type: '', text: '' })}
                    className="w-12 ml-auto text-lg p-2 bg-blue-600 text-white leading-none hover:text-white"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-5">
            <div ref={reportsSectionRef} className="rounded-3xl border border-slate-800 bg-slate-900 p-5 shadow-2xl sm:p-6">
              <div className="mb-2 flex items-start gap-4">
                <div className="rounded-2xl bg-slate-800 p-4 text-yellow-200">
                  <i className="fa-solid fa-chart-column text-lg lg:text-xl"></i>
                </div>
                <div>
                  <h2 className="text-sm lg:text-lg font-black text-yellow-200">{t('applauseTrend')}</h2>
                  <p className="mt-1 text-xs lg:text-sm text-gray-300">{t('monthlyAnalytics')}</p>
                </div>
              </div>
              <div className="h-72">
                <Line data={chartData} options={chartOptions} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};